<template>
  <div class="gs-app gv-categories">
    <loading
      :active.sync="isLoading"
      :is-full-page="fullPageLoader"
      :color="loaderColor"
    ></loading>
    <div class="w-full gv-categories__content">
      <v-wait for="video">
        <half-circle-spinner
          style="display: block; margin: 0 auto"
          :animation-duration="1000"
          :size="60"
          color="#3d4852"
          slot="waiting"
        />

        <div class="gv-categories__stack">
          <gs-card class="gv-categories__header">
            <span class="gv-categories__eyebrow">GrooveVideo</span>
            <h1 class="gv-categories__title">Categories</h1>
            <p class="gv-categories__subtitle">
              Group videos so filters and analytics stay tidy.
            </p>
          </gs-card>

          <gs-card
            v-if="categories && categories.length"
            class="gv-categories__list"
          >
            <div
              v-for="category in categories"
              :key="category.id"
              class="gv-categories__group"
            >
              <div class="gv-categories__row">
                <div class="gv-categories__row-main">
                  <span class="gv-categories__name">{{ category.category }}</span>
                  <span
                    v-if="category.child && category.child.length"
                    class="gv-categories__count"
                  >
                    {{ category.child.length }}
                    {{ category.child.length === 1 ? 'subcategory' : 'subcategories' }}
                  </span>
                </div>
                <div v-if="category.id != 1" class="gv-categories__row-actions">
                  <gs-button
                    variant="ghost"
                    size="sm"
                    @click="openCategoryModal(category.id)"
                  >
                    <gs-icon name="edit" size="15" />
                    {{ $t('frontend_edit') }}
                  </gs-button>
                  <gs-button
                    v-if="!category.is_automated"
                    variant="ghost"
                    size="sm"
                    class="is-destructive"
                    @click="deleteCategory(category.id)"
                  >
                    <gs-icon name="trash" size="15" />
                    {{ $t('frontend_delete') }}
                  </gs-button>
                </div>
              </div>

              <div
                v-for="child in category.child"
                :key="child.id"
                class="gv-categories__row gv-categories__row--child"
              >
                <div class="gv-categories__row-main">
                  <span class="gv-categories__bullet" aria-hidden="true"></span>
                  <span class="gv-categories__name">{{ child.category }}</span>
                </div>
                <div class="gv-categories__row-actions">
                  <gs-button
                    variant="ghost"
                    size="sm"
                    @click="openCategoryModal(child.id)"
                  >
                    <gs-icon name="edit" size="15" />
                    {{ $t('frontend_edit') }}
                  </gs-button>
                  <gs-button
                    v-if="!child.is_automated"
                    variant="ghost"
                    size="sm"
                    class="is-destructive"
                    @click="deleteCategory(child.id)"
                  >
                    <gs-icon name="trash" size="15" />
                    {{ $t('frontend_delete') }}
                  </gs-button>
                </div>
              </div>
            </div>
          </gs-card>

          <gs-card v-else class="gv-categories__empty">
            <h2 class="gv-categories__empty-title">No categories yet</h2>
            <p class="gv-categories__empty-body">
              Create a category to group videos and keep filters tidy.
            </p>
            <gs-button variant="outline" @click="openCategoryModal()">
              <gs-icon name="plus" size="16" />
              New category
            </gs-button>
          </gs-card>
        </div>
      </v-wait>

      <portal to="headerContent">
        <div class="flex items-center gv-categories__header-actions">
          <gs-button variant="primary" class="w-40 mr-4 gv-categories__add-action" @click="openCategoryModal()">
            <gs-icon name="plus" size="16" />
            New category
          </gs-button>
        </div>
      </portal>
      <!-- Create category modal -->
      <add-new-category
        v-if="open"
        :categories="categories"
        :categoryId="categoryId"
        @on-save="saveCategory"
        @close-modal="open = false"
        ref="addNewCategory"
      ></add-new-category>
      <!-- Create category modal -->
    </div>
  </div>
</template>

<style>
.dropdown-toggle {
  background-image: none !important;
  border: 1px solid dodgerblue !important;
  border-radius: 30px;
}

.dropdown-toggle:hover {
  background: transparent;
}
.custom-width {
  width: 6.6rem;
}
</style>

<style>
.gv-categories__content {
  padding: 24px;
}

.gv-categories__stack {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.gv-categories__add-action {
  min-height: 40px;
}

.gv-categories__header {
  padding: 24px;
}

.gv-categories__eyebrow {
  display: block;
  margin-bottom: 6px;
  color: var(--gs-muted-foreground);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.gv-categories__title {
  margin: 0 0 6px;
  color: var(--gs-foreground);
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
}

.gv-categories__subtitle {
  margin: 0;
  max-width: 46rem;
  color: var(--gs-muted-foreground);
  font-size: 14px;
}

.gv-categories__list {
  padding: 8px 0;
}

.gv-categories__group + .gv-categories__group {
  border-top: 1px solid var(--gs-border);
}

.gv-categories__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 24px;
  min-height: 52px;
}

.gv-categories__row + .gv-categories__row {
  border-top: 1px solid var(--gs-border);
}

.gv-categories__row--child {
  padding-left: 48px;
}

.gv-categories__row-main {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.gv-categories__bullet {
  flex: none;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--gs-muted-foreground);
}

.gv-categories__name {
  color: var(--gs-foreground);
  font-size: 14px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gv-categories__row--child .gv-categories__name {
  font-weight: 500;
}

.gv-categories__count {
  flex: none;
  padding: 2px 8px;
  border-radius: var(--gs-radius-sm);
  background: var(--gs-muted);
  color: var(--gs-muted-foreground);
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.gv-categories__row-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: none;
}

.gv-categories__row-actions .gs-button {
  gap: 6px;
}

.gv-categories__empty {
  padding: 40px 24px;
  text-align: center;
}

.gv-categories__empty-title {
  margin: 0 0 6px;
  color: var(--gs-foreground);
  font-size: 16px;
  font-weight: 700;
}

.gv-categories__empty-body {
  margin: 0 0 16px;
  color: var(--gs-muted-foreground);
  font-size: 14px;
}

@media (max-width: 640px) {
  .gv-categories__content {
    padding: 16px;
  }

  .gv-categories__header {
    padding: 18px;
  }

  .gv-categories__row {
    align-items: flex-start;
    flex-direction: column;
    padding: 12px 18px;
  }

  .gv-categories__row--child {
    padding-left: 34px;
  }

  .gv-categories__header-actions,
  .gv-categories__header-actions .gv-categories__add-action {
    width: 100%;
  }

  .gv-categories__header-actions .gv-categories__add-action {
    margin-right: 0 !important;
  }
}
</style>

<script>
import Swal from 'sweetalert2';
import Loading from 'vue-loading-overlay';
import router from '@/router';
import themeConfig from '@/theme/themeConfig.js';
import 'vue-loading-overlay/dist/vue-loading.css';
import Notify from '@groovepages/gd-lib/notify';
import Snapshot from '@/assets/images/icons/Focus.svg';
import store from '@/state';
import VideoEdit from '@/models/VideoEdit';
import Category from '@/models/Category';
import AddNewCategory from '@/pages/videos/AddNewCategory';

export default {
  middleware: 'auth',
  layout: 'defaultWithoutScroll',

  metaInfo() {
    return { title: 'Video Category' };
  },
  data() {
    return {
      open: false,
      rerenderComponent: true,
      tableComponentKey: 0,
      funnel: null,
      funnels: [],
      video: null,
      categories: [],
      itemsPerPage: 5,
      categoryId: null,
      fullPageLoader: true,
      isLoading: false,
      loaderColor: themeConfig.appPrimaryColor,
      categoryTableColumns: ['category', 'actions'],
      categoryTableOptions: {
        sortIcon: {
          base: 'fa',
          is: 'fa-sort',
          up: 'fa-sort-asc',
          down: 'fa-sort-desc',
        },
        sortable: ['category'],
        headings: {
          category: this.$t('frontend_groovevideo_videos_analytics_table_name'),
          actions: this.$t('frontend_groovevideo_actions')
          // category: () =>
          //   this.$t('frontend_groovevideo_videos_analytics_table_name'),
        },
        filterByColumn: false,
        filterable: false,
        perPage: 10000,
        rowClassCallback(myRowObject) {
          return 'hover:bg-grey-50 funnelRow';
        },
      },
    };
  },

  components: {
    Loading,
    AddNewCategory,
    Snapshot,
  },
  methods: {
    sortCells(event) {
      this.categories = this.categories.map((el) => {
        if (el.child && el.child.length > 0) {
          el.child.sort((a, b) => {
            const fa = a.category.toLowerCase();
            const fb = b.category.toLowerCase();

            if (fa < fb) {
              return -1;
            }
            if (fa > fb) {
              return 1;
            }
            return 0;
          });

          if (!event.ascending) {
            el.child.reverse();
          }
        }
        return el;
      });
    },

    showLoader() {
      if (!this.isLoading) {
        this.isLoading = true;
      }
    },
    hideLoader() {
      if (this.isLoading) {
        this.isLoading = false;
      }
    },

    async getCategories() {
      // Get video category
      this.categories = await Category.$get();
    },
    async saveCategory() {
      this.showLoader();
      await this.getCategories();
      await this.$refs.addNewCategory?.closeCategoryModal();
      this.hideLoader();
    },
    async deleteCategory(categoryId) {
      const { value: accept } = await Swal.fire({
        title: this.$t('frontend_sure'),
        text: this.$t('frontend_non_revertable'),
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: this.$t('frontend_delete_confirm'),
      });

      if (accept) {
        this.showLoader();
        const category = await Category.$find(categoryId);
        const parentId = category.parent_id;
        await category.delete();

        if (parentId !== 0) {
          const index = this.categories.findIndex((el) => el.id === parentId);

          if (this.categories[index] && this.categories[index].child) {
            this.categories[index].child = this.categories[index].child.filter(
              (el) => el.id !== categoryId
            );
          }
        } else {
          await this.getCategories();
        }

        this.hideLoader();
      }
    },
    openCategoryModal(id = null) {
      this.categoryId = id;
      this.open = true;
    },
  },
  async created() {
    this.showLoader();
    this.$wait.start('categories');

    this.getCategories();

    this.$wait.end('categories');
    this.hideLoader();
  },
  async mounted() {},
  computed: {
    admin() {
      return this.$store.state.user.admin;
    },
  },
};
</script>

<style lang="scss">
.dropdown-button-container {
  display: flex;
  align-items: center;

  .btnx {
    border-radius: 5px 0px 0px 5px;
  }

  .btn-drop {
    border-radius: 0px 5px 5px 0px;
    border-left: 1px solid rgba(255, 255, 255, 0.2);
  }
}

#funnels-list-view {
  .VueTables--client table {
    overflow: auto !important;
  }

  .VueTables__search-field {
    margin-bottom: 10px;
  }

  .VueTables__heading {
    color: #8f92ab;
  }

  .VueTables__sort-icon {
    color: #8f92ab;
    margin-left: 8px;
  }

  td {
    font-weight: 200;
  }
  td,
  th {
    outline: none;
  }

  .VueTables__child-row-toggler--closed::before {
    content: url("data:image/svg+xml; utf8, <svg width='20' height='20' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'><path d='m3.707031 7l-.707031.707031 6.125 6.125 1.875 1.875 1.875-1.875 6.125-6.125-.707031-.707031-6.125 6.125-1.167969 1.167969-1.167969-1.167969-6.125-6.125' /></svg>");
    padding-right: 3px;
    vertical-align: middle;
  }

  .VueTables__child-row {
    text-align: center;
    border-bottom: 1px solid #ddd;
    border-left: 5px solid #fd6481;
  }

  .VueTables__child-row-toggler--open::before {
    content: url("data:image/svg+xml; utf8, <svg width='20' height='20' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'><path d='m3.707031 1044.3622l-.707031-.707 6.125-6.125 1.875001-1.875 1.875 1.875 6.125 6.125-.707032.707-6.125-6.125-1.167968-1.168-1.167969 1.168-6.125 6.125' transform='translate(0-1030.3622)'/></svg>");
    padding-right: 3px;
    vertical-align: middle;
  }

  .VueTables__table {
    border-collapse: collapse;
    border-spacing: 0;
    width: 100%;
  }

  .VueTables__row {
    border-bottom: 1px solid #ddd;
  }

  .VueTables__table th,
  td {
    text-align: left;
    padding: 16px;
  }

  tr.VueTables__child-row > td {
    padding: 0px !important;
  }

  thead {
    border-bottom: 1px solid #ddd;
  }

  .vue-pagination-ad {
    text-align: center;
  }

  .VueTables__limit {
    display: none;
  }

  .VuePagination__count {
    display: none;
  }

  .expandIcon {
    display: none;
  }

  .funnelRow:hover .expandIcon {
    display: block !important;
  }
}
</style>
